"use client";

import { Dialog, DialogContent } from "@punchup/ui-common/shadcn/ui/dialog";
import { Skeleton } from "@punchup/ui-common/shadcn/ui/skeleton";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
} from "@punchup/ui-common/shadcn/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@punchup/ui-common/shadcn/ui/sheet";
import { Button } from "@punchup/ui-common/shadcn/ui/button";
import { PunchupSpinner } from "@punchup/ui-common/punchup/primitives/feedback/PunchupSpinner";
import { CircleCheck, X, Zap } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGate } from "statsig-react";
import mixpanel from "mixpanel-browser";
import {
  PaymentEvents,
  trackEvent,
  trackVenuePurchaseConversion,
} from "@punchup/analytics";
import { useTixologi, useUser } from "@punchup/data/legacy/hooks";
import { getGa4ClientId, getGa4SessionId } from "@punchup/analytics";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { useTixologiWidget } from "../hooks/widget";
import {
  PunchupIdentityPayload,
  buildPunchupIdentityPayload,
  buildTixologiPurchaseEventProps,
  readUtmParams,
} from "./tixologiAnalytics";
import {
  downloadTicketsPDF,
  downloadAppleWallet,
  triggerFileDownload,
  openAppleWallet,
} from "../services/tickets";
import { tixologiWidgetSrc } from "../services/prewarm";
import { isTixologiWidgetLink, widgetOriginOf } from "../widget-origin";
import { WidgetLoadTelemetrySession } from "./widgetLoadTelemetry";
import { useToast } from "@punchup/ui-common/shadcn/ui/use-toast";

// Statsig gate controlling the desktop right-side drawer rollout. When off, desktop
// falls back to the original centered modal. Create this gate in Statsig.
const SIDE_DRAWER_GATE = "tixologi_widget_side_drawer";

// Read measurement IDs published by VenueAnalyticsScripts's inline gtag
// snippet. See docs/specs/tixologi-widget.md → "GA4 cross-domain handoff".
type WindowWithVenueGa4 = Window &
  typeof globalThis & {
    __punchupVenueGa4Ids?: string[];
  };

// Helper function to detect mobile devices
const isMobile = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768;
};

// How long after a legacy `height` message (old widget builds that don't post
// `page-ready`) we reveal the iframe anyway. New builds post `page-ready` when real
// content is up, which reveals immediately and cancels this.
const LEGACY_HEIGHT_REVEAL_MS = 1200;
// Absolute safety net from iframe onLoad, in case no message ever arrives.
const LOAD_REVEAL_FALLBACK_MS = 4000;

// DNS + TLS to the widget origin are off the critical path when warmed ahead of the
// navigation; the iframe then starts on a hot connection.
//
// The latch is keyed BY ORIGIN, not the boolean it replaces: the origin is now resolved per
// link (a preview swap, or a developer's localhost link sitting next to a canonical one), so a
// single boolean would warm whichever origin happened to arrive first and then silently skip
// every other one for the life of the page.
//
// It is also warmed later than it used to be — when the chosen show's link is known, rather
// than at mount — because before a show is chosen there is no link to resolve an origin from.
// Little is lost: the mount-time version warmed `new URL(NEXT_PUBLIC_TIXOLOGI_URL).origin`,
// which is the bare apex host and not where the widget is served, and the real early warm is
// `prewarmTixologiCheckout` on Buy-button hover (services/prewarm.ts).
// The widget's first data call (the event fetch) goes to the ticketing gateway, an origin
// this document never touches — so it pays full DNS+TLS at the exact moment the buyer is
// staring at the iframe. Warming it alongside the widget origin overlaps that setup with
// the iframe's own load. Resolved defensively: a misconfigured env produces a non-URL
// string ("undefined/...") that must degrade to no hint, never a crash.
const ticketingApiOrigin = (): string | null => {
  const base = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!base) return null;
  try {
    return new URL(base).origin;
  } catch {
    return null;
  }
};

// What the buyer stares at until the widget posts page-ready and the iframe fades in.
// A silhouette of the event page (hero, title lines, ticket rows) matching the widget's
// own EventSkeleton, so the pre-reveal wait and the widget's boot state read as one
// continuous load instead of a spinner that swaps into a different-looking page.
const FastCheckoutSkeleton = () => (
  <div
    aria-hidden
    data-testid="fast-checkout-skeleton"
    className="absolute inset-0 overflow-hidden bg-white p-6"
  >
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-col gap-6 md:flex-row">
        <Skeleton className="aspect-[7/6] w-full rounded-lg md:w-2/5" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-6 w-10/12" />
          <Skeleton className="h-6 w-7/12" />
          <Skeleton className="h-4 w-5/12" />
          <Skeleton className="h-4 w-8/12" />
        </div>
      </div>
      <div className="mt-8 space-y-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-lg border border-neutral-200 p-6"
          >
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const preconnectedOrigins = new Set<string>();
const preconnectToWidget = (origin: string) => {
  if (typeof document === "undefined" || preconnectedOrigins.has(origin))
    return;
  preconnectedOrigins.add(origin);
  for (const rel of ["preconnect", "dns-prefetch"]) {
    const link = document.createElement("link");
    link.rel = rel;
    link.href = origin;
    document.head.appendChild(link);
  }
};

export const TixologiWidgetIframe = () => {
  const { isOpen, show, close: closeTixologiWidget } = useTixologiWidget();
  const { tixologiData } = useTixologi({
    id: undefined,
    isComedian: undefined,
  });
  const user = useUser();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const legacyRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Buyer identity for the iframe — delivered by postMessage (not the URL) so the
  // iframe URL is cacheable and no token/PII lands in a URL. Held in a ref because
  // the postMessage senders (message listener, onLoad) are bound once on mount.
  const identityPayloadRef = useRef<PunchupIdentityPayload | null>(null);
  // A content key for the payload above. The identity effect can re-run on every
  // render (buyer identity derives from an async, unstably-referenced session), so
  // this drives the re-delivery effect off the payload's CONTENT, not its object
  // identity — the effect fires only when the identity actually changes, never on
  // every render. It exists so a late-resolving identity (the Supabase session
  // commonly lands after the widget's ready ping and our onLoad — the two senders
  // that would otherwise carry it) still reaches the iframe; without it the
  // completed payload never arrives and email/name never prefill.
  // A monotonic revision, bumped only when the identity CONTENT changes, drives
  // re-delivery. The serialized key that detects the change contains the token +
  // email, so it lives in a ref (like identityPayloadRef) and never in React state —
  // keeping the credential out of DevTools / state snapshots.
  const [identityRevision, setIdentityRevision] = useState(0);
  const lastIdentityKeyRef = useRef("");
  // The origin every postMessage to the iframe is addressed at, resolved PER LINK from the same
  // rewritten link the src is built from. Held in a ref for the same reason as the identity
  // payload above: `handleMessage` is bound once on mount and would otherwise close over the
  // first render's value. Null means "no origin we can prove" and nothing is posted at all —
  // '*' is not an option for a payload carrying an auth token and an email address.
  const widgetOriginRef = useRef<string | null>(null);
  const [src, setSrc] = useState<string>("");
  // Mirror of `src` for the retry reload: the telemetry callbacks are bound
  // once per open, and the open effect can run before the src state update
  // from the same commit lands — a ref sidesteps the stale closure.
  const srcRef = useRef("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [transactionId, setTransactionId] = useState<string>("");
  const [ticketCount, setTicketCount] = useState<number>(1);
  const [guestToken, setGuestToken] = useState<string>("");
  // When the show has delayed ticket delivery, the widget posts downloadDisabled=true
  // on purchase-complete so tickets can't be downloaded/added to wallet before the
  // delivery window — hides the Download (PDF) and Add to Apple Wallet buttons.
  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const [isMobileView, setIsMobileView] = useState(isMobile());
  // Load-lifecycle telemetry (Mixpanel + Sentry) for the blank-iframe problem.
  // One tracker object behind one ref because the message listener and onLoad
  // are bound once on mount — see widgetLoadTelemetry.ts. Lazy-initialized so
  // re-renders don't allocate throwaway sessions.
  const telemetryRef = useRef<WidgetLoadTelemetrySession | null>(null);
  if (telemetryRef.current === null) {
    telemetryRef.current = new WidgetLoadTelemetrySession();
  }
  const telemetry = telemetryRef.current;
  // True once the load timeout fired without a real-content signal: shows the
  // "taking longer than expected" panel with the open-in-new-tab escape hatch.
  const [slowLoadFallback, setSlowLoadFallback] = useState(false);
  // Which download is in flight, so only the clicked button shows a spinner while
  // both stay disabled (guards against a concurrent PDF + wallet download).
  const [downloadingType, setDownloadingType] = useState<
    "pdf" | "wallet" | null
  >(null);
  const { toast } = useToast();

  // Gate the desktop right-side drawer; defaults to false (centered modal) until loaded.
  const { value: sideDrawerEnabled } = useGate(SIDE_DRAWER_GATE);

  const tixologiToken = tixologiData?.token;

  // The postMessage listener below is registered once on mount, so it would
  // otherwise close over a stale `show`. Mirror the latest NON-NULL show into
  // a ref so the purchase handler and telemetry read the current show without
  // re-binding. The name says the semantics: this is the LAST show, not a
  // faithful mirror — it deliberately survives close() (which nulls `show`
  // before the [isOpen] close branch logs abandonment), same as displayShow
  // does for the UI. Do not use it to ask "is the modal open".
  const lastShowRef = useRef(show);
  useEffect(() => {
    if (show) lastShowRef.current = show;
  }, [show]);

  // Read by the telemetry session at emit time (never snapshotted at start),
  // so it only touches refs and globals — safe inside the once-bound handlers.
  const getTelemetryContext = () => ({
    show_id: lastShowRef.current?.id ?? null,
    show_title: lastShowRef.current?.title ?? null,
    tixologi_event_id: lastShowRef.current?.tixologiEventId ?? null,
    venue_id: lastShowRef.current?.venueId ?? null,
    // Names, not just ids, so a timeout report is self-describing ("which
    // venue is having blank-checkout problems") without an external join —
    // same as the purchase event's `Venue` prop. Public listing data, not PII.
    venue_name: lastShowRef.current?.venue ?? null,
    widget_origin: widgetOriginRef.current,
    hostname: typeof window === "undefined" ? null : window.location.hostname,
    is_mobile_view: isMobile(),
  });

  // close() clears `show` synchronously, which would unmount the drawer instantly and
  // skip the Sheet's slide-out animation. Retain the last show so we keep rendering
  // through the close transition (shouldRender unmounts us after the animation).
  const [displayShow, setDisplayShow] = useState(show);
  useEffect(() => {
    if (show) setDisplayShow(show);
  }, [show]);

  // Handle window resizing for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // the show will only be null if the modal is not open yet
    if (!show) return;

    // This guard tests the STORED link and must stay that way: `ticket_link` fuses ENVIRONMENT
    // (the origin) with IDENTITY (the event id), and only identity is row-scoped — so every row
    // carries a CANONICAL widget origin even while a preview widget is wired. Teaching it about
    // previews would be widening the meaning of "is this our checkout link" to move something
    // the swap below already moves. See packages/tixologi/src/widget-origin.ts.
    if (isTixologiWidgetLink(show.ticketLink)) {
      // The iframe URL carries only `isPunchup=true` (a constant mode flag) — no
      // token, email, name, or analytics id. All buyer identity is delivered by
      // postMessage after load (postIdentityHandoff), so the URL is the same for
      // every user and the SSR page caches at the edge, and no auth token or PII
      // ends up in a URL / CDN cache / server log / referer.
      const initialReferrer = document.referrer;
      const mixpanelPersistence = localStorage.getItem(
        `mp_${mixpanel.get_config("token")}_mixpanel`
      );
      const mixpanelPersistenceJson = JSON.parse(mixpanelPersistence || "{}");
      const mixpanelReferrer = mixpanelPersistenceJson.$initial_referrer;
      const { utmSource, utmMedium, utmCampaign } = readUtmParams(
        window.location.search,
        mixpanelPersistenceJson
      );

      const nextIdentity = buildPunchupIdentityPayload({
        hostname: window.location.hostname,
        token: tixologiToken,
        email: user?.email,
        name: user?.user_metadata.name,
        userId: user?.id,
        mixpanelDistinctId: mixpanel.get_distinct_id(),
        initialReferrer: initialReferrer || mixpanelReferrer || undefined,
        utmSource,
        utmMedium,
        utmCampaign,
      });
      identityPayloadRef.current = nextIdentity;
      // Compare the serialized content (not the object) so an unchanged identity
      // re-built on a later render does not re-fire delivery — only a real change
      // (e.g. the session resolving with the buyer's email) bumps the revision.
      const nextIdentityKey = nextIdentity ? JSON.stringify(nextIdentity) : "";
      if (nextIdentityKey !== lastIdentityKeyRef.current) {
        lastIdentityKeyRef.current = nextIdentityKey;
        setIdentityRevision((revision) => revision + 1);
      }

      // Order is load-bearing: guard the stored link first. The preview swap happens INSIDE
      // both `widgetOriginOf` and `tixologiWidgetSrc`, each from the same stored link, so the
      // postMessage target and the iframe src cannot drift apart. Addressing the handoffs at
      // the canonical host while the iframe loads a preview host makes the browser DROP both
      // messages in silence — every buyer then checks out as an anonymous guest, which reads
      // as a widget bug.
      const widgetOrigin = widgetOriginOf(show.ticketLink);
      widgetOriginRef.current = widgetOrigin;
      if (widgetOrigin) preconnectToWidget(widgetOrigin);
      const apiOrigin = ticketingApiOrigin();
      if (apiOrigin) preconnectToWidget(apiOrigin);

      const widgetSrc = tixologiWidgetSrc(show.ticketLink);
      srcRef.current = widgetSrc;
      setSrc(widgetSrc);
    }
  }, [show, tixologiToken, user]);

  // Force-reload a DEAD load (the telemetry session decides deadness — no
  // widget-ready, no height). Resetting src restarts the whole cycle, exactly
  // like handleBuyMoreTickets; the stale reveal timers from the abandoned
  // cycle are cleared so a blind reveal can't fire mid-retry.
  const forceReloadForRetry = () => {
    if (legacyRevealTimerRef.current) {
      clearTimeout(legacyRevealTimerRef.current);
      legacyRevealTimerRef.current = null;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    if (iframeRef.current && srcRef.current) {
      iframeRef.current.src = srcRef.current;
    }
  };

  // Handle open/close states
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setShouldRender(true);
      setIframeReady(false);
      setSlowLoadFallback(false);
      // Time the load ONLY when this open will actually mount the widget
      // iframe. Buttons can open the modal via isTixologiCheckoutShow
      // (event-id OR host) while the iframe mounts only for widget-host links
      // — that mismatch renders nothing (see the render guard below), and
      // timing it would emit phantom timeouts and a false Sentry alert for an
      // iframe that never existed.
      //
      // start() is idempotent (this branch has no cleanup, so strict mode runs
      // it twice in dev) and emits nothing — it only stamps t0 and arms the
      // ready-timeout. A dead load gets up to MAX_LOAD_RETRIES silent forced
      // reloads (onRetry) before the panel shows (onGiveUp); an alive-but-slow
      // widget skips retries and goes straight to the panel.
      if (isTixologiWidgetLink(lastShowRef.current?.ticketLink ?? "")) {
        telemetry.start(getTelemetryContext, {
          onRetry: forceReloadForRetry,
          onGiveUp: () => setSlowLoadFallback(true),
        });
      }
      // Mount the iframe with the modal — the navigation doesn't need layout, and the
      // old 50ms "let the container render first" delay just postponed the request.
      setIsLoaded(true);
    } else {
      document.body.style.overflow = "";
      // Stamp abandonment at the close click, not inside the 400ms unmount
      // delay below (which is itself cleaned up and may never run).
      telemetry.recordClosed();
      setSlowLoadFallback(false);
      // Reveal timers from this session must not leak into the next open.
      if (legacyRevealTimerRef.current) {
        clearTimeout(legacyRevealTimerRef.current);
        legacyRevealTimerRef.current = null;
      }
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      // When closing, wait for animation to finish before unloading iframe
      const timer = setTimeout(() => {
        setIsLoaded(false);
        setShouldRender(false);
        setIframeReady(false);
      }, 400); // Outlast the Sheet's 300ms slide-out so the close animation isn't cut off

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reveal helpers. The widget posts `height` on EVERY DOM mutation — including its own
  // loading spinner — so a height message only proves "something rendered", not "content
  // is up". Revealing on it directly is what stacked our spinner on top of the widget's.
  // New widget builds post an explicit `page-ready` once real content committed; height
  // messages only arm a legacy fallback for older builds.
  const markIframeReady = (source: "page-ready" | "legacy-height") => {
    // Both callers prove real content (page-ready explicitly; legacy-height
    // via the 1.2s settle window), so they cancel the load timeout and clear
    // the slow-load panel. The 4s blind reveal deliberately does neither —
    // it calls setIframeReady directly in handleIframeLoad.
    telemetry.recordReady(source);
    setSlowLoadFallback(false);
    if (legacyRevealTimerRef.current) {
      clearTimeout(legacyRevealTimerRef.current);
      legacyRevealTimerRef.current = null;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    setIframeReady(true);
  };

  // Buyer-identity handoff (parent → iframe). Posted to the widget's origin (never
  // '*', since it carries email + auth token) after load, replacing the old URL
  // params. Sent both when the widget pings that it's ready and again on our own
  // onLoad, so a missed ping still resolves. The widget adopts the first delivery.
  // Reads only refs, so it is stable across renders (empty deps). Memoized so the
  // re-delivery effect below can list it as a dependency without re-running.
  const postIdentityHandoff = useCallback(() => {
    const targetOrigin = widgetOriginRef.current;
    if (!targetOrigin || !iframeRef.current?.contentWindow) return;
    if (!identityPayloadRef.current) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "punchup:identity", payload: identityPayloadRef.current },
      targetOrigin
    );
  }, []);

  // Re-deliver identity whenever its content changes. The async Supabase session
  // often lands after the widget's ready ping and our onLoad have already fired —
  // the two senders that would otherwise carry it — so without this the completed
  // payload (email/name/token) never reaches the iframe and checkout opens blank.
  // The widget adopts the first non-empty delivery (first-wins) and postMessage
  // no-ops until the iframe exists, so re-posting is safe and idempotent.
  useEffect(() => {
    if (identityPayloadRef.current) postIdentityHandoff();
  }, [identityRevision, postIdentityHandoff]);

  const handleMessage = (event: MessageEvent) => {
    if (event.source !== iframeRef.current?.contentWindow) return;

    if (event.data?.action === "punchup-widget-ready") {
      telemetry.recordWidgetReady();
      postIdentityHandoff();
    }

    if (event.data?.action === "page-ready") {
      markIframeReady("page-ready");
    }

    // isNaN(undefined) is true, so a message without height falls through;
    // ?. keeps a null-data message from throwing inside the listener.
    if (!isNaN(event.data?.height)) {
      telemetry.recordHeightSeen();
      if (!legacyRevealTimerRef.current) {
        legacyRevealTimerRef.current = setTimeout(
          () => markIframeReady("legacy-height"),
          LEGACY_HEIGHT_REVEAL_MS
        );
      }
    }

    if (event.data?.action === "purchase-complete") {
      setPurchaseComplete(true);
      setTransactionId(event.data.transactionCode);
      setTicketCount(event.data.ticketCount || 1);
      setGuestToken(event.data.guestToken || "");
      setDownloadDisabled(event.data.downloadDisabled === true);

      // Mirror the conversion back to the parent so it is recorded under the
      // SAME Mixpanel distinct_id as the venue-page view and "Buy Tickets
      // Clicked" event — giving us a native end-to-end funnel even though the
      // checkout itself happens inside a cross-origin iframe. Read `lastShowRef`
      // (not `show`) because this listener is registered once on mount.
      trackEvent(
        PaymentEvents.TIXOLOGI_PURCHASE_COMPLETED,
        buildTixologiPurchaseEventProps(lastShowRef.current, {
          transactionCode: event.data.transactionCode,
          ticketCount: event.data.ticketCount,
        })
      );

      // Fire the venue's own Google Ads purchase conversion (D1/D14) — the
      // helper itself already fails open (never throws, silently no-ops on
      // a page with no published venue tags), but this call is wrapped too
      // so the purchase-complete UI and the Mixpanel mirror above are never
      // put at risk by this addition. `total` is a decimal-dollars STRING
      // ("12.34") today; `Number(...)` (not parseFloat) so a malformed total
      // ("1,234.56", "12.34abc") becomes NaN and falls into the helper's
      // omit-value path instead of recording a wrong dollar amount, and a
      // numeric total (contract drift) is passed through rather than
      // silently dropped. An omitted or empty total passes `undefined`, never NaN.
      try {
        const rawTotal: unknown = event.data.total;
        trackVenuePurchaseConversion({
          value:
            typeof rawTotal === "number"
              ? rawTotal
              : typeof rawTotal === "string" && rawTotal.trim() !== ""
                ? Number(rawTotal)
                : undefined,
          currency: "USD",
          transactionId: event.data.transactionCode,
        });
      } catch (error) {
        logException(
          error instanceof Error
            ? error
            : new Error(
                "trackVenuePurchaseConversion threw from purchase-complete"
              ),
          { tags: { feature: SentryFeatureTags.GoogleAds } }
        );
      }
    }
  };

  // GA4 cross-domain handoff (parent → iframe). The GA4 linker can't
  // auto-decorate iframe `src` URLs set by JS, so once the iframe has
  // loaded we postMessage the parent's GA4 client_id + session_id over
  // to the iframe document, which uses them as the override on its
  // first `gtag('config', …)` call. Keeping identifiers out of the
  // URL avoids leaking them into caches, server logs, and referers.
  // Iframe-side listener lives in the tixologi repo — see
  // docs/specs/tixologi-widget.md → "GA4 cross-domain handoff".
  // If the listener is not yet deployed, the message is silently
  // discarded — no regression vs. the URL-param approach.
  const postGa4Handoff = async () => {
    const targetOrigin = widgetOriginRef.current;
    if (!targetOrigin || !iframeRef.current?.contentWindow) return;
    const gaIds = (window as WindowWithVenueGa4).__punchupVenueGa4Ids ?? [];
    for (const id of gaIds) {
      const [clientId, sessionId] = await Promise.all([
        getGa4ClientId(id),
        getGa4SessionId(id),
      ]);
      if (clientId && sessionId) {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: "punchup:ga4-handoff",
            measurementId: id,
            clientId,
            sessionId,
          },
          targetOrigin
        );
        return;
      }
    }
  };

  // Handle iframe load event
  const handleIframeLoad = () => {
    telemetry.recordIframeLoad();

    // Fire-and-forget the GA4 identity handoff. We don't block iframe
    // readiness on this — the worst case (iframe-side listener not yet
    // deployed, or gtag never loaded on the parent) is that the iframe
    // starts a fresh GA4 session, which matches today's baseline.
    void postGa4Handoff();

    // Deliver buyer identity (mixpanel id, email, name, token) now too — belt and
    // suspenders alongside the widget's ready-ping, in case the ping was missed.
    postIdentityHandoff();

    // Clear any previous timeout to prevent accumulation
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    // Safety net: reveal even if no message ever arrives (blocked postMessage, ancient
    // widget build). page-ready / the legacy height fallback normally win well before.
    loadTimeoutRef.current = setTimeout(() => {
      // Blind reveal: no proof of content, so it's recorded as such and does
      // NOT clear the slow-load fallback panel — only a real signal does.
      telemetry.recordReady("load-fallback");
      setIframeReady(true);
      loadTimeoutRef.current = null;
    }, LOAD_REVEAL_FALLBACK_MS);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Cleanup reveal timers on unmount
  useEffect(() => {
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      if (legacyRevealTimerRef.current) {
        clearTimeout(legacyRevealTimerRef.current);
      }
      telemetry.dispose();
    };
  }, []);

  const handleBuyMoreTickets = () => {
    setPurchaseComplete(false);
    setDownloadDisabled(false);
    // Re-time the reload as a new attempt; the old content stays visible while
    // it loads (iframeReady is deliberately not reset here — existing behavior),
    // so a slow reload's panel may cover live content until page-ready clears it.
    setSlowLoadFallback(false);
    telemetry.restart();
    if (iframeRef.current) {
      iframeRef.current.src = src;
    }
  };

  const handleOnClose = () => {
    setPurchaseComplete(false);
    closeTixologiWidget();
  };

  const handleDownloadPDF = async () => {
    if (!guestToken) {
      toast({
        title: "Error",
        description: "Authentication required. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setDownloadingType("pdf");
      const blob = await downloadTicketsPDF({
        transactionCode: transactionId,
        token: guestToken,
      });
      triggerFileDownload(blob, `tickets-${transactionId}.pdf`);
      toast({
        title: "Success",
        description: "Tickets downloaded successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to download PDF tickets:", error);
      toast({
        title: "Error",
        description: "Failed to download PDF tickets. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingType(null);
    }
  };

  const handleDownloadAppleWallet = async () => {
    if (!guestToken) {
      toast({
        title: "Error",
        description: "Authentication required. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setDownloadingType("wallet");
      const blob = await downloadAppleWallet({
        transactionCode: transactionId,
        ticketCount,
        token: guestToken,
      });
      const contentType =
        ticketCount > 1
          ? "application/vnd.apple.pkpasses"
          : "application/vnd.apple.pkpass";
      openAppleWallet(blob, contentType);
      toast({
        title: "Success",
        description: "Apple Wallet passes downloaded successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to download Apple Wallet passes:", error);
      toast({
        title: "Error",
        description:
          "Failed to download Apple Wallet passes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingType(null);
    }
  };

  if (!src || !shouldRender || !displayShow) {
    return null;
  }

  // Shared content for both drawer and dialog
  const contentComponent = (
    <>
      <div className="sticky top-0 flex justify-between items-center p-4 border-b bg-white z-10">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          Fast Checkout
        </h3>
        {isMobileView ? (
          <DrawerClose className="p-1 rounded-full hover:bg-neutral-100">
            <X className="w-5 h-5" />
          </DrawerClose>
        ) : (
          <button
            onClick={handleOnClose}
            className="p-1 rounded-full hover:bg-neutral-100"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {purchaseComplete ? (
        <div className="flex flex-col items-center gap-4 p-6 pt-10 md:pt-14 overflow-auto md:flex-1 md:min-h-0">
          <CircleCheck className="w-16 h-16 text-green-600" strokeWidth={1.5} />
          <div className="text-2xl font-bold">Purchase complete!</div>
          {transactionId && (
            <div className="text-sm bg-muted px-3 py-2 rounded-md">
              Order Number: <span className="font-bold">{transactionId}</span>
            </div>
          )}
          <div className="text-base text-center text-muted-foreground">
            Expect an email with your ticket information in the next 10 minutes.
          </div>

          <div className="mt-2 flex w-full max-w-xs flex-col gap-2">
            <Button
              onClick={handleBuyMoreTickets}
              disabled={downloadingType !== null}
            >
              Buy more tickets
            </Button>
            {!downloadDisabled && (
              <>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleDownloadPDF}
                  disabled={downloadingType !== null}
                >
                  {downloadingType === "pdf" && (
                    <PunchupSpinner size={16} variant="current" />
                  )}
                  Download tickets (PDF)
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleDownloadAppleWallet}
                  disabled={downloadingType !== null}
                >
                  {downloadingType === "wallet" && (
                    <PunchupSpinner size={16} variant="current" />
                  )}
                  Add to Apple Wallet
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className="relative overflow-auto md:flex-1 md:min-h-0"
          style={{
            height: isMobileView ? "calc(92vh - 100px)" : undefined,
          }}
        >
          {/* Pre-sized placeholder to reduce CLS */}
          <FastCheckoutSkeleton />

          {/* Iframe with opacity transition */}
          {isLoaded && (
            <iframe
              ref={iframeRef}
              id={`tixologi-iframe-${displayShow.id}`}
              src={src}
              width="100%"
              height="100%"
              style={{
                border: "none",
                height: "100%",
                minHeight: isMobileView ? undefined : "600px",
                opacity: iframeReady ? 1 : 0,
                transition: "opacity 0.5s ease",
                position: "relative",
                zIndex: 1,
              }}
              loading="eager"
              allow="payment"
              onLoad={handleIframeLoad}
            />
          )}

          {/* Slow-load fallback — the give-up state after the silent retry
              budget is spent (or immediately, for an alive-but-stuck widget).
              Fills the whole iframe area ABOVE the iframe (z-10 vs its zIndex
              1): there is nothing worth showing behind it (the skeleton or a
              blank blind-revealed document), and full cover means the 4s blind
              reveal can't expose an empty frame around it. Only a real content
              signal (page-ready / legacy-height) clears it. Links the
              canonical ticket page, not `src` — src carries the isPunchup=true
              embedded-mode flag and may point at a preview origin. */}
          {slowLoadFallback && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white p-6 text-center">
              <div className="text-base text-muted-foreground">
                Taking longer than expected…
              </div>
              <Button asChild variant="outline">
                <a
                  href={displayShow.ticketLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => telemetry.recordOpenedInNewTab()}
                >
                  Open checkout in a new tab
                </a>
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  // Use different components for mobile and desktop
  if (isMobileView) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => !open && handleOnClose()}
        shouldScaleBackground={false}
        modal={true}
        dismissible={true}
      >
        <DrawerContent className="p-0 pb-safe max-h-[92vh] overflow-hidden">
          {contentComponent}
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop, gate ON: right-to-left side drawer (Sheet) so the iframe gets the full
  // browser height instead of a centered modal capped at 90vh. [&>button.absolute]:hidden
  // suppresses only the Sheet's built-in close (the absolutely-positioned corner button)
  // — we keep the custom close in the header.
  if (sideDrawerEnabled) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleOnClose()}>
        <SheetContent
          side="right"
          // Width: floor of 600px, target 90vw, cap 1000px. The Sheet's `right` variant
          // ships `sm:max-w-sm` (384px) which was silently squeezing the drawer narrow.
          // Two things defeat it: `min-w-[600px]` (min-width wins over max-width in the
          // CSS cascade, so it overrides the 384px clamp) and `sm:max-w-[1000px]` (same
          // breakpoint as `sm:max-w-sm`, so tailwind-merge replaces it rather than
          // keeping both) — letting the drawer grow past the floor up to 1000px.
          // No h-full: the variant's top-app-banner + bottom-0 set the height,
          // so the drawer ends at the viewport bottom below an app banner.
          className="w-[90vw] min-w-[600px] max-w-[1000px] sm:max-w-[1000px] p-0 gap-0 flex flex-col overflow-hidden border-none [&>button.absolute]:hidden"
        >
          <SheetTitle className="sr-only">Fast Checkout</SheetTitle>
          {contentComponent}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop, gate OFF: original centered modal
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleOnClose()}>
      <DialogContent className="p-0 max-w-[1200px] w-[90%] max-h-[90vh] flex flex-col overflow-hidden rounded-xl border-none">
        {contentComponent}
      </DialogContent>
    </Dialog>
  );
};
